import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import {
  listByCliente,
  uploadDocumento,
  downloadDocumento,
  deleteDocumento,
  TIPO_DOCUMENTO,
  TIPO_DOCUMENTO_LABEL,
  ACCEPT_MIME,
  MAX_SIZE_BYTES,
  formatBytes,
} from "../../services/documentosService";
import { listRoteiros } from "../../services/roteirosService";
import ConfirmDialog from "../ui/ConfirmDialog";

const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png"];

// Seção "Documentos" do modal de cliente.
// Só é renderizada em modo edição (quando já existe clienteId no backend).
export default function ClienteDocumentosSection({ clienteId }) {
  const { C } = useTheme();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado do upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [tipo, setTipo] = useState(TIPO_DOCUMENTO.Passaporte);
  const [roteiroId, setRoteiroId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Roteiros do cliente (para o dropdown opcional de vínculo)
  const [roteirosCliente, setRoteirosCliente] = useState([]);

  // Confirmação de exclusão
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function reload() {
    if (!clienteId) return;
    setLoading(true);
    try {
      const list = await listByCliente(clienteId);
      setDocs(list);
      setError(null);
    } catch {
      setError("Não foi possível carregar os documentos.");
    } finally {
      setLoading(false);
    }
  }

  async function reloadRoteiros() {
    if (!clienteId) return;
    try {
      const list = await listRoteiros();
      setRoteirosCliente(list.filter((r) => r.clienteId === clienteId));
    } catch {
      setRoteirosCliente([]);
    }
  }

  useEffect(() => {
    reload();
    reloadRoteiros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  function openUpload() {
    setArquivo(null);
    setTipo(TIPO_DOCUMENTO.Passaporte);
    setRoteiroId("");
    setUploadError(null);
    setUploadOpen(true);
  }

  function onFileChange(e) {
    setUploadError(null);
    const f = e.target.files?.[0];
    if (!f) {
      setArquivo(null);
      return;
    }
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setUploadError("Formato inválido. Aceitos: PDF, JPG, JPEG, PNG.");
      setArquivo(null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setUploadError("Arquivo excede 10 MB.");
      setArquivo(null);
      return;
    }
    setArquivo(f);
  }

  async function handleUpload() {
    if (!arquivo) {
      setUploadError("Selecione um arquivo.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      await uploadDocumento({
        arquivo,
        clienteId,
        tipo,
        roteiroId: roteiroId ? Number(roteiroId) : null,
      });
      setUploadOpen(false);
      await reload();
    } catch (err) {
      setUploadError(
        err?.response?.data?.message || "Erro ao enviar o arquivo."
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(doc) {
    try {
      await downloadDocumento(doc.id, doc.nomeOriginal);
    } catch {
      setError("Erro ao baixar o arquivo.");
    }
  }

  async function handleDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteDocumento(toDelete.id);
      setToDelete(null);
      await reload();
    } catch {
      setError("Erro ao excluir o documento.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div
        style={{
          marginTop: 20,
          paddingTop: 16,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 700,
              color: C.text,
              letterSpacing: 0.3,
            }}
          >
            Documentos
          </h3>
          <button
            type="button"
            onClick={openUpload}
            style={{
              padding: "7px 14px",
              borderRadius: 8,
              border: "none",
              background: C.accent,
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            + Upload Documento
          </button>
        </div>

        {loading && (
          <div style={{ fontSize: 12, color: C.textMuted, padding: "8px 0" }}>
            Carregando...
          </div>
        )}
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: C.redDim,
              color: C.red,
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            {error}
          </div>
        )}

        {!loading && docs.length === 0 && !error && (
          <div
            style={{
              fontSize: 12,
              color: C.textMuted,
              fontStyle: "italic",
              padding: "8px 0",
            }}
          >
            Nenhum documento enviado ainda.
          </div>
        )}

        {docs.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {docs.map((d) => (
              <div
                key={d.id}
                style={{
                  padding: "10px 12px",
                  background: C.bg,
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={d.nomeOriginal}
                  >
                    {d.nomeOriginal}
                  </div>
                  <div
                    style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}
                  >
                    {TIPO_DOCUMENTO_LABEL[d.tipo]}
                    {d.roteiroTitulo ? ` · ${d.roteiroTitulo}` : ""} ·{" "}
                    {formatBytes(d.tamanhoBytes)} ·{" "}
                    {new Date(d.dataUpload).toLocaleDateString("pt-BR")}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => handleDownload(d)}
                    style={iconBtn(C)}
                    title="Download"
                  >
                    ⬇
                  </button>
                  <button
                    type="button"
                    onClick={() => setToDelete(d)}
                    style={{ ...iconBtn(C), color: C.red }}
                    title="Excluir"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {uploadOpen && (
        <div
          onClick={() => !uploading && setUploadOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 1600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.bgCard,
              borderRadius: 14,
              border: `1px solid ${C.border}`,
              padding: 22,
              width: "100%",
              maxWidth: 420,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: 14,
                fontSize: 15,
                fontWeight: 700,
                color: C.text,
              }}
            >
              Upload de documento
            </h3>

            <label style={labelStyle(C)}>Arquivo</label>
            <input
              type="file"
              accept={ACCEPT_MIME}
              onChange={onFileChange}
              style={{
                ...inputStyle(C),
                padding: 8,
              }}
            />
            <div
              style={{
                fontSize: 10,
                color: C.textDim,
                marginTop: 4,
              }}
            >
              PDF, JPG, JPEG ou PNG — máximo 10 MB.
            </div>

            <label style={{ ...labelStyle(C), marginTop: 12 }}>Tipo</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(Number(e.target.value))}
              style={inputStyle(C)}
            >
              {Object.entries(TIPO_DOCUMENTO).map(([k, v]) => (
                <option key={v} value={v}>
                  {k}
                </option>
              ))}
            </select>

            <label style={{ ...labelStyle(C), marginTop: 12 }}>
              Vincular a roteiro (opcional)
            </label>
            <select
              value={roteiroId}
              onChange={(e) => setRoteiroId(e.target.value)}
              style={inputStyle(C)}
            >
              <option value="">—</option>
              {roteirosCliente.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.titulo}
                </option>
              ))}
            </select>

            {uploadError && (
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 12px",
                  background: C.redDim,
                  color: C.red,
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                {uploadError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                disabled={uploading}
                onClick={() => setUploadOpen(false)}
                style={btnSecondary(C)}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={uploading || !arquivo}
                onClick={handleUpload}
                style={{
                  ...btnPrimary(C),
                  opacity: uploading || !arquivo ? 0.6 : 1,
                }}
              >
                {uploading ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!toDelete}
        title="Excluir documento?"
        message={`Remover "${toDelete?.nomeOriginal}"? Essa ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}

function labelStyle(C) {
  return {
    display: "block",
    fontSize: 10,
    color: C.textMuted,
    fontWeight: 600,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  };
}

function inputStyle(C) {
  return {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.inputBg || C.bgCardHover,
    color: C.text,
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  };
}

function iconBtn(C) {
  return {
    padding: "6px 10px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.textMuted,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  };
}

function btnSecondary(C) {
  return {
    padding: "8px 16px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.bgCard,
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
  };
}

function btnPrimary(C) {
  return {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    background: C.accent,
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
  };
}
